import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FirebaseConfig } from '../config/FirebaseConfig';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Box,
  Divider
} from '@mui/material';
import CircularLoading from '../components/CircularLoading';
import { colors } from '../components/Theme/WebTheme';

export default function DisputeDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { fetchDisputes, addDisputeMessage, editDispute, fetchBookings } = api;
  const disputedata = useSelector(state => state.disputedata.disputes);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const settings = useSelector(state => state.settingsdata?.settings);
  const dispute = disputedata ? disputedata.find(d => d.id === id) : null;

  const [refundAmount, setRefundAmount] = useState('');
  const [refundDest, setRefundDest] = useState('wallet');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!disputedata) {
      dispatch(fetchDisputes(null, 'admin'));
    }
    if (!bookinglistdata?.bookings) {
      dispatch(fetchBookings());
    }
  }, [dispatch, disputedata, fetchDisputes, bookinglistdata?.bookings, fetchBookings]);

  const booking = (bookinglistdata?.bookings && dispute?.booking_id)
    ? bookinglistdata.bookings.find(bk => bk.id === dispute.booking_id)
    : null;

  useEffect(() => {
    if (dispute) {
      const amt = (dispute.refund_amount !== undefined && dispute.refund_amount !== null && dispute.refund_amount !== '')
        ? dispute.refund_amount
        : (booking && booking.refund_amount !== undefined && booking.refund_amount !== null ? booking.refund_amount : null);

      if (amt !== null && amt !== undefined) {
        setRefundAmount(amt.toString());
      } else if (booking) {
        setRefundAmount(prev => prev || (booking.customer_paid || booking.trip_cost || '').toString());
      }

      const dest = dispute.refund_destination || dispute.refund_method || booking?.refund_method || booking?.refund_destination;
      if (dest) {
        setRefundDest(dest);
      }
    }
  }, [dispute, booking]);

  const handleRefund = async () => {
    if (!refundAmount || isNaN(refundAmount)) return alert(t('invalid_amount', 'Invalid amount'));
    
    // Call cloud function to process refund
    try {
      const response = await fetch(`https://${FirebaseConfig.projectId}.web.app/stripe-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: dispute.booking_id,
          amount: parseFloat(refundAmount),
          destination: refundDest
        })
      });
      const data = await response.json();
      if (data.success) {
        alert(t('refund_successful', 'Refund successful!'));
        const updatedDispute = {
          ...dispute,
          status: 'Closed',
          refund_status: 'Refunded',
          refund_amount: parseFloat(refundAmount),
          refund_destination: refundDest,
          refund_method: refundDest,
          refunded_at: new Date().getTime()
        };
        dispatch(editDispute(updatedDispute, 'Update'));
      } else {
        alert((t('refund_failed', 'Refund failed')) + ": " + data.error);
      }
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const handleSendMessage = () => {
    if (!message) return;
    dispatch(addDisputeMessage(id, {
      sender: 'admin',
      text: message,
    }));
    setMessage('');
  };

  const closeDispute = () => {
    dispatch(editDispute({ ...dispute, status: 'Closed' }, 'Update'));
    navigate('/disputes');
  };

  if (!dispute) return <CircularLoading />;

  const cardStyle = {
    p: { xs: 2.5, sm: 3, md: 4 },
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.4)'
  };

  const gradientButton = {
    backgroundColor: colors.MAIN_COLOR,
    borderRadius: '24px',
    color: 'white',
    padding: '10px 28px',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '0.95rem',
    boxShadow: '0 4px 14px 0 rgba(46, 60, 37, 0.39)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(46, 60, 37, 0.5)',
    },
    '&:disabled': {
      background: '#e0e0e0',
      boxShadow: 'none',
      color: '#9e9e9e'
    }
  };

  const outlinedButton = {
    borderRadius: '24px',
    padding: '10px 28px',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '0.95rem',
    border: '2px solid #f44336',
    color: '#f44336',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      border: '2px solid #d32f2f',
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
      transform: 'translateY(-2px)',
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
      <Button 
        onClick={() => navigate('/disputes')} 
        sx={{ 
          mb: 3, 
          color: '#64748b', 
          fontWeight: '600',
          textTransform: 'none',
          '&:hover': { backgroundColor: 'transparent', color: '#0f172a' }
        }}
      >
        {t('back_to_disputes', '← Back to Disputes')}
      </Button>
      
      <Grid container spacing={4} sx={{ alignItems: 'flex-start' }}>
        {/* Left Column: Details & Actions */}
        <Grid item xs={12} md={5}>
          <Paper sx={cardStyle}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
              {t('dispute_overview', 'Dispute Overview')}
            </Typography>
            
            <Box sx={{ mb: 4, p: { xs: 1.5, sm: 2 }, backgroundColor: '#f1f5f9', borderRadius: '12px', overflow: 'hidden' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap', mr: 1 }}>
                  {t('booking_id', 'Booking ID')}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ flex: 1, minWidth: 0, gap: 1 }}>
                  <Typography 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#0f172a', 
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      textAlign: 'right',
                      minWidth: 0,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}
                  >
                    {dispute.booking_id}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => navigate(`/bookings/bookingdetails/${dispute.booking_id}`, { state: { from: `/dispute-details/${id}` } })}
                    sx={{ 
                      borderRadius: '12px', 
                      textTransform: 'none', 
                      fontSize: '0.75rem', 
                      py: 0.25, 
                      px: 1,
                      minWidth: 'auto',
                      flexShrink: 0
                    }}
                  >
                    {t('view_booking', 'View')}
                  </Button>
                </Box>
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap', mr: 1 }}>
                  {t('passenger', 'Passenger')}
                </Typography>
                <Typography 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#0f172a', 
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    textAlign: 'right',
                    flex: 1,
                    minWidth: 0,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                  }}
                >
                  {dispute.passenger_id}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {t('ticket_status', 'Ticket Status')}
                </Typography>
                <Box sx={{ 
                  backgroundColor: dispute.status === 'Closed' ? '#dcfce7' : '#fef3c7', 
                  color: dispute.status === 'Closed' ? '#166534' : '#92400e',
                  px: 1.5, py: 0.5, borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem',
                  flexShrink: 0
                }}>
                  {dispute.status}
                </Box>
              </Box>

              {(() => {
                const isRefunded = dispute.refund_status === 'Refunded' || booking?.refund_status === 'refunded' || dispute.refund_amount !== undefined || (booking && booking.refund_amount !== undefined);
                const refundStatusText = isRefunded ? 'Refunded' : (dispute.refund_status || 'None');
                const displayRefundAmt = (dispute.refund_amount !== undefined && dispute.refund_amount !== null && dispute.refund_amount !== '')
                  ? dispute.refund_amount
                  : (booking && booking.refund_amount !== undefined && booking.refund_amount !== null ? booking.refund_amount : refundAmount);
                const displayRefundDest = dispute.refund_destination || dispute.refund_method || booking?.refund_destination || booking?.refund_method || refundDest;

                return (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {t('refund_status', 'Refund Status')}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: isRefunded ? '#059669' : '#0f172a', flexShrink: 0 }}>
                        {refundStatusText}
                      </Typography>
                    </Box>

                    {isRefunded && (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
                          <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {t('refund_amount', 'Refund Amount')}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: '#059669' }}>
                            {settings?.symbol ? `${settings.symbol}${parseFloat(displayRefundAmt || 0).toFixed(settings?.decimal || 2)}` : (displayRefundAmt || '-')}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
                          <Typography sx={{ color: '#475569', fontSize: '0.9rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {t('refund_destination', 'Refund Destination')}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {displayRefundDest === 'original' ? t('original_payment_stripe', 'Original Payment (Stripe)') : t('passenger_wallet', 'Passenger Wallet')}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </>
                );
              })()}
            </Box>

            {dispute.subject && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 700, mb: 1, letterSpacing: '0.5px' }}>
                  {t('customer_complaint', 'Customer Complaint')}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5, fontSize: '1rem' }}>
                  {dispute.subject}
                </Typography>
                <Typography sx={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.6, backgroundColor: '#f8fafc', p: 2, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {dispute.body}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mt: 3, mb: 2 }}>
              {t('resolution_actions', 'Resolution Actions')}
            </Typography>
            
            {(() => {
              const isRefunded = dispute.refund_status === 'Refunded' || booking?.refund_status === 'refunded' || dispute.refund_amount !== undefined || (booking && booking.refund_amount !== undefined);
              return (
                <>
                  <TextField 
                    label={t('refund_amount', 'Refund Amount')} 
                    type="number" 
                    fullWidth 
                    variant="outlined" 
                    value={refundAmount} 
                    disabled={isRefunded}
                    onChange={e => setRefundAmount(e.target.value)} 
                    sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                  />
                  
                  <FormControl fullWidth sx={{ mb: 4 }} disabled={isRefunded}>
                    <InputLabel>{t('refund_destination', 'Refund Destination')}</InputLabel>
                    <Select 
                      value={refundDest} 
                      onChange={e => setRefundDest(e.target.value)} 
                      sx={{ borderRadius: '12px' }} 
                      label={t('refund_destination', 'Refund Destination')}
                    >
                      <MenuItem value="wallet">{t('passenger_wallet', 'Passenger Wallet')}</MenuItem>
                      <MenuItem value="original">{t('original_payment_stripe', 'Original Payment (Stripe)')}</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box display="flex" gap={2} mt={3} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button 
                      sx={{ ...gradientButton, flex: 1 }}
                      onClick={handleRefund}
                      disabled={isRefunded}
                    >
                      {t('process_refund', 'Process Refund')}
                    </Button>
                    
                    <Button 
                      sx={{ ...outlinedButton, flex: 1 }}
                      onClick={closeDispute}
                      disabled={dispute.status === 'Closed'}
                    >
                      {t('close_dispute', 'Close Ticket')}
                    </Button>
                  </Box>
                </>
              );
            })()}
          </Paper>
        </Grid>
        
        {/* Right Column: Chat Interface */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ ...cardStyle, overflow: 'hidden', p: 0, height: 'calc(100vh - 140px)', minHeight: '600px' }}>
            <Box sx={{ p: 3, backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {t('conversation_history', 'Communication Log')}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                {t('chat_securely_dispute', 'Chat securely with the passenger regarding this dispute.')}
              </Typography>
            </Box>
            
            <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, p: 3, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dispute.messages && Object.values(dispute.messages).map((msg, idx) => {
                const isAdmin = msg.sender === 'admin';
                return (
                  <Box key={idx} sx={{ 
                    alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                    maxWidth: '75%'
                  }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', mb: 0.5, ml: isAdmin ? 0 : 1, mr: isAdmin ? 1 : 0, display: 'block', textAlign: isAdmin ? 'right' : 'left' }}>
                      {isAdmin ? t('you_admin', 'You (Admin)') : t('passenger', 'Passenger')}
                    </Typography>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: isAdmin ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                      backgroundColor: isAdmin ? colors.MAIN_COLOR : '#ffffff',
                      color: isAdmin ? '#ffffff' : '#334155',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      border: isAdmin ? 'none' : '1px solid #e2e8f0'
                    }}>
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            
            <Box sx={{ p: 3, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField 
                fullWidth 
                placeholder={t('type_message_placeholder', 'Type a message to the passenger...')} 
                variant="outlined"
                value={message} 
                onChange={e => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: '24px',
                    backgroundColor: '#f1f5f9',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: colors.MAIN_COLOR }
                  } 
                }} 
              />
              <Button 
                onClick={handleSendMessage}
                disabled={!message.trim()}
                sx={{ 
                  minWidth: '50px',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: colors.MAIN_COLOR,
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(46, 60, 37, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(46, 60, 37, 0.4)',
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                    boxShadow: 'none'
                  }
                }}
              >
                ➤
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
